/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  SearchSort,
  SearchSortField,
  SearchSortOrder,
} from "~/generated/graphql";
import { SortDirection } from "~/components/SortSelector";

type Props = {
  field?: SearchSortField;
  order?: SearchSortOrder;
  updateQueryState: (params: SearchSort & { page: string }) => void;
};

export function useEstateSort({ field, order, updateQueryState }: Props) {
  const sortParams = React.useMemo(() => {
    return {
      field,
      order,
    };
  }, [field, order]);

  const [sortField, setSortField] = React.useState<SearchSortField>(
    sortParams.field ?? SearchSortField.Cost
  );
  const [sortDirection, setSortDirection] = React.useState<SearchSortOrder>(
    sortParams.order ?? SearchSortOrder.Dsc
  );

  React.useEffect(() => {
    setSortField(sortParams.field ?? SearchSortField.Cost);
    setSortDirection(sortParams.order ?? SearchSortOrder.Dsc);
  }, [sortParams]);

  const onSortSelect = (filter: SearchSortField) => {
    const isDefaultDescending = [
      SearchSortField.Cost,
      SearchSortField.Forecast,
      SearchSortField.SavingsAmount,
    ].includes(filter);
    updateQueryState({
      field: filter,
      order: isDefaultDescending ? SearchSortOrder.Dsc : SearchSortOrder.Asc,
      page: "0",
    });
  };

  const onSortOrderChange = (direction: SortDirection) => {
    updateQueryState({
      field: sortField,
      order:
        direction === SortDirection.Ascending
          ? SearchSortOrder.Asc
          : SearchSortOrder.Dsc,
      page: "0",
    });
  };

  return {
    sortField,
    sortDirection,
    onSortSelect,
    onSortOrderChange,
  };
}
